import { Permission } from 'constants/permissions'
import * as DEMO from 'constants/demo'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { getDemoToken, updateBearerToken } from 'utils/api'
import { AuthHook, User } from '../types'

/**
 * Module-level singleton state for demo token
 * Ensures token is fetched only once across all hook instances
 */
let demoToken: string | null = null
let tokenError: Error | undefined
let tokenFetchPromise: Promise<void> | null = null

/**
 * Fetches demo token exactly once, no matter how many times it's called
 * Subsequent calls return the cached promise/result
 */
async function fetchDemoTokenOnce(): Promise<void> {
  // If already have token, do nothing
  if (demoToken) {
    return Promise.resolve()
  }

  // If fetch already in progress, wait for it
  if (tokenFetchPromise) {
    return tokenFetchPromise
  }

  // Start new fetch and cache the promise
  tokenFetchPromise = (async () => {
    try {
      const res = await getDemoToken()
      if (res.data.token) {
        demoToken = res.data.token
        updateBearerToken(demoToken)
      } else {
        throw new Error('Demo Token API response was OK, but demo token not found on response.')
      }
    } catch (err) {
      console.error('Failed to fetch demo token:', err)
      tokenError = err instanceof Error ? err : new Error('Failed to fetch demo token')
      throw tokenError
    }
  })()

  return tokenFetchPromise
}

/**
 * Reset module-level state (for testing only)
 * @internal
 */
export function resetDemoAuthState(): void {
  demoToken = null
  tokenError = undefined
  tokenFetchPromise = null
}

/**
 * useDemoAuth
 *
 * Hook for external demo authentication.
 * Provides mock authentication with demo permissions.
 * Fetches a demo token from the API only once during initial site load,
 * using module-level singleton to prevent redundant API calls.
 */
export default function useDemoAuth(): AuthHook {
  const [tokenInitialized, setTokenInitialized] = useState(false)

  const permissions = useMemo(() => {
    const demoPermissions = DEMO.ID_TOKEN_PERMISSIONS.permissions as Permission[]
    return new Set(demoPermissions)
  }, [])

  // Initialize demo authentication by fetching the demo token
  // Uses module-level singleton to ensure this only happens once
  useEffect(() => {
    // If token already loaded, mark as initialized immediately
    if (demoToken) {
      setTokenInitialized(true)
      return
    }

    // Otherwise, fetch the token (will use cached promise if already fetching)
    fetchDemoTokenOnce()
      .then(() => {
        setTokenInitialized(true)
      })
      .catch(() => {
        // Error already logged in fetchDemoTokenOnce
        setTokenInitialized(true) // Still mark as initialized to stop loading state
      })
  }, [])

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return permissions.has(permission)
    },
    [permissions]
  )

  const loginWithRedirect = useCallback(async (): Promise<void> => {
    // No-op for demo mode
    return Promise.resolve()
  }, [])

  const logout = useCallback((): void => {
    // No-op for demo mode
  }, [])

  const user: User = useMemo(
    () => ({
      org_id: DEMO.ORG_ID,
      email: DEMO.EMAIL,
    }),
    []
  )

  return useMemo(
    () => ({
      isAuthenticated: true,
      isLoading: !tokenInitialized,
      user,
      error: tokenError,
      isInternalUser: false,
      supportsLogin: false,
      hasPermission,
      loginWithRedirect,
      logout,
    }),
    [tokenInitialized, user, hasPermission, loginWithRedirect, logout]
  )
}
